import {MediaCardMetadata} from '@wandb/weave/common/types/media';
import {useMemo} from 'react';

import {RunHistoryRow} from '../../../types/run';
import {getMediaMetadata} from '../../../util/media';
import {RunWithHistoryAndMediaWandb} from '../../MediaCard';
import {MediaBrowserPanelConfig} from '../common';
import {StepMeta} from '../types';
import {getCurrentStepAndSliderValue} from '../utils/stepUtils';
import {useStepMeta} from './useStepMeta';

export type StepCalculationResult = {
  stepMeta: StepMeta;
  currentStep: number;
  currentSliderValue: number;
  firstMediaMetadata: MediaCardMetadata | null;
};

/**
 * Calculates step ranges, slider values, and current step/index state.
 *
 * This hook handles:
 * - Aggregating step metadata across all runs
 * - Resolving slider step collisions
 * - Determining current step based on config
 * - Fetching first media metadata for sizing calculations
 *
 * @param runsWithMedia - Runs that have media data
 * @param runStepsMap - Pre-computed map of run name to history rows
 * @param mediaKeys - Media keys to check
 * @param config - Panel configuration with step/slider settings
 * @returns Object with step metadata, current step, and first media metadata
 */
export function useStepCalculation(
  runsWithMedia: RunWithHistoryAndMediaWandb[],
  runStepsMap: Map<string, RunHistoryRow[]>,
  mediaKeys: string[] | undefined,
  config: Pick<
    MediaBrowserPanelConfig,
    'mediaSliderKey' | 'stepIndex' | 'snapToExistingStep'
  >
): StepCalculationResult {
  const {
    mediaSliderKey,
    stepIndex: stepFromConfig,
    snapToExistingStep = true,
  } = config;

  // Calculate step metadata across all runs
  const stepMeta = useStepMeta(
    runsWithMedia,
    mediaSliderKey ?? '_step',
    snapToExistingStep,
    runStepsMap,
    mediaKeys
  );

  // Determine current step and slider value based on config
  const {currentSliderValue, currentStep} = useMemo(() => {
    return getCurrentStepAndSliderValue(
      stepMeta,
      stepFromConfig,
      snapToExistingStep,
      mediaSliderKey
    );
  }, [stepMeta, stepFromConfig, snapToExistingStep, mediaSliderKey]);

  // Get first media metadata for sizing calculations
  const firstMediaMetadata = useMemo(() => {
    const firstMediaKey = mediaKeys?.[0] ?? null;
    const firstHistory = runsWithMedia[0]?.history ?? null;

    return firstMediaKey != null && firstHistory != null
      ? getMediaMetadata(firstHistory, firstMediaKey, currentStep)
      : null;
  }, [runsWithMedia, mediaKeys, currentStep]);

  return {
    stepMeta,
    currentStep,
    currentSliderValue,
    firstMediaMetadata,
  };
}
